import { authAdapter } from '@/app/_auth/auth'
import type { UniversalUser } from '@/app/_auth/auth-adapter'
import { AuthContext } from '@/app/_auth/auth-provider'
import { AUTH_PROVIDER_DATA, AuthProviderID } from '@/app/_auth/steps/_common/login-buttons'
import React from 'react'
import { useEffectOnce } from 'react-use'

export function useAuth() {
  const authContext = React.useContext(AuthContext)
  if (!authContext) throw new Error('useAuth must be used within AuthContext.Provider')

  return authContext
}

export function useLogoutOnMount() {
  useEffectOnce(() => {
    if (!authAdapter) {
      return
    }

    if (authAdapter.isAuthenticated()) {
      authAdapter.signOut().catch((err) => {
        console.error('Failed to sign out:', err)
      })
    }
  })
}

export function useOnAuthStateChanged({
  onLogin,
  onLogout,
}: {
  onLogin?: (user?: UniversalUser) => void
  onLogout?: () => void
}) {
  useEffectOnce(() => {
    if (!authAdapter) {
      return
    }

    const unsubscribe = authAdapter.onAuthStateChanged((user) => {
      if (user && onLogin) {
        onLogin(user)
      } else if (onLogout) {
        onLogout()
      }
    })

    return unsubscribe
  })
}

export function useAuthError(): {
  providerName: string
  verifiedProvider: AuthProviderID[]
  email: string
} {
  const { authError } = useAuth()

  if (!authError?.customData) throw new Error('AUTH_ERROR_DATA_NOT_FOUND')

  const { email, _tokenResponse } = authError.customData

  if (!email) throw new Error('AUTH_ERROR_EMAIL_NOT_FOUND')
  if (!_tokenResponse) throw new Error('AUTH_ERROR_TOKEN_RESPONSE_NOT_FOUND')

  const { providerId, verifiedProvider = [] } = _tokenResponse

  if (!providerId) throw new Error('AUTH_ERROR_PROVIDER_ID_NOT_FOUND')
  if (verifiedProvider.length === 0) throw new Error('AUTH_ERROR_VERIFIED_PROVIDER_NOT_FOUND')

  return { providerName: AUTH_PROVIDER_DATA[providerId].displayName, verifiedProvider, email }
}
